// @flow
import * as React from 'react';
import idx from 'idx';
import { graphql, compose } from 'react-apollo';

import GET_AUTO_MESSAGE_WITHOUT_SENDS_QUERY from 'admin/components/AdminAutoMessages/queries/GetAutoMessageWithoutSends.gql';
import GET_AUTO_MESSAGES_FOR_ORG_QUERY from 'admin/components/AdminAutoMessages/queries/GetAutoMessagesForOrg.gql';
import GET_AUTO_MESSAGE_SFTP_CREDENTIALS_FOR_ORG from 'admin/components/AdminAutoMessageSftpCredentials/queries/AdminAutoMessageSftpCredentialsQuery.gql';
import SAVE_AUTO_MESSAGE_MUTATION from './queries/SaveAutoMessageMutation.gql';
import ADMIN_NEW_AUTO_MESSAGE_ORGS_QUERY from './queries/AdminNewAutoMessageOrgsQuery.gql';
import CurrentUser from 'auth/CurrentUser';

import type { GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages } from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type {
  AdminNewAutoMessageOrgs_organizations,
  SaveAutoMessageInput,
} from './queries/__generated__/AdminNewAutoMessageOrgs';
import type { AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials } from 'admin/components/AdminAutoMessageSftpCredentials/queries/__generated__/AdminAutoMessageSftpCredentials';

type Props = {
  children: (props: {
    autoMessage: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages,
    loading: ?boolean,
    orgs: ?(AdminNewAutoMessageOrgs_organizations[]),
    save: (input: SaveAutoMessageInput) => Promise<*>,
    possibleSftpCredentials: ?(AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials[]),
  }) => React.Node,
  messageUuid: ?string,
  viewingOrgUuid: ?string,

  // from gql
  autoMessage: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages,
  orgs: ?(AdminNewAutoMessageOrgs_organizations[]),
  possibleSftpCredentials: ?(AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials[]),
  loadingMessage: boolean,
  loadingOrgs: boolean,
  loadingCreds: boolean,
  mutate: ({
    variables: {
      input: SaveAutoMessageInput,
    },
    refetchQueries: *[],
  }) => Promise<*>,
};

class AdminNewAutoMessageContainer extends React.Component<Props, void> {
  render() {
    const {
      loadingMessage,
      autoMessage,
      children,
      mutate,
      orgs,
      loadingOrgs,
      viewingOrgUuid,
      loadingCreds,
      possibleSftpCredentials,
    } = this.props;
    const currentUser = CurrentUser.getUser();
    const hasCustomizedMessaging =
      currentUser && currentUser.hasFeature('customized_messaging.web.jun-19');

    return children({
      loading: loadingMessage || loadingOrgs || loadingCreds,
      hasCustomizedMessaging,
      autoMessage,
      possibleSftpCredentials,
      orgs,
      viewingOrgUuid,
      save: input =>
        mutate({
          variables: { input },
          refetchQueries: [
            {
              query: GET_AUTO_MESSAGES_FOR_ORG_QUERY,
              variables: { organizationUUID: viewingOrgUuid },
            },
          ],
        }),
    });
  }
}

const withExistingMessage = graphql(GET_AUTO_MESSAGE_WITHOUT_SENDS_QUERY, {
  skip: ({ messageUuid }) => !messageUuid,
  options: ({ messageUuid }) => ({
    variables: { messageUuid },
  }),
  props: ({ data }) => ({
    loadingMessage: data.loading,
    autoMessage: idx(data, _ => _.autoMessaging.autoMessages.autoMessages[0]),
  }),
});

const withOrgs = graphql(ADMIN_NEW_AUTO_MESSAGE_ORGS_QUERY, {
  options: ({ viewingOrgUuid }) => ({
    variables: { organizationUUID: viewingOrgUuid },
  }),
  props: ({ data }) => {
    const topLevelOrg = idx(data, _ => _.organizations[0]);
    const orgs = topLevelOrg ? [topLevelOrg, ...(topLevelOrg.childOrganizations || [])] : [];
    return {
      loadingOrgs: data.loading,
      orgs,
    };
  },
});

const withAutoMessageSftpCredentials = graphql(GET_AUTO_MESSAGE_SFTP_CREDENTIALS_FOR_ORG, {
  options: ({ viewingOrgUuid }) => ({
    variables: { orgUuid: viewingOrgUuid },
  }),
  props: ({ data }) => {
    return {
      loadingCreds: data.loading,
      possibleSftpCredentials: idx(data, _ => _.autoMessaging.sftpCredentials),
    };
  },
});

const withMutation = graphql(SAVE_AUTO_MESSAGE_MUTATION);

export { AdminNewAutoMessageContainer };

export default compose(
  withExistingMessage,
  withOrgs,
  withAutoMessageSftpCredentials,
  withMutation
)(AdminNewAutoMessageContainer);
